import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, Siren } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Feral Animal Control",
  "Farm Culling Program",
  "Property Assessment",
  "Urgent Livestock Protection",
  "Other",
];

const REGIONS = ["Gippsland", "North East", "Western District", "Mallee", "Yarra Valley", "Central Highlands", "Riverina NSW", "Murray NSW"];

const EMPTY = { name: "", email: "", confirmEmail: "", phone: "", service: SERVICES[0], location: "", message: "" };

const AU_PHONE = /^(?:(?:\+61\s?4|04)\d{2}\s?\d{3}\s?\d{3}|0[2378]\s?\d{4}\s?\d{4})$/;

const inputCls =
  "w-full border border-bone/15 bg-obsidian px-4 py-3 text-sm text-bone placeholder:text-euca/50 outline-none transition-colors duration-300 focus:border-thermal";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.email.trim().toLowerCase() !== form.confirmEmail.trim().toLowerCase()) {
      errs.email = "Email addresses do not match — please re-check.";
    }
    if (form.phone.trim() && !AU_PHONE.test(form.phone.trim())) {
      errs.phone = "Enter an Australian mobile (04XX XXX XXX) or landline (0X XXXX XXXX).";
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch within 24–48 hours.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0]?.msg : null;
      toast.error(msg || "Could not send your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-16">
      <section className="mx-auto max-w-7xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Contact · Enquiry</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            Put us on
            <br />
            <span className="text-thermal">your boundary.</span>
          </h1>
        </FadeIn>

        <div className="mt-16 grid gap-10 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <div className="border border-thermal/15 bg-field p-6 sm:p-10">
              {sent ? (
                <div className="py-16 text-center" data-testid="contact-success-message">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-thermal" />
                  <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-bone">Enquiry received</h2>
                  <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-euca">
                    Thanks {form.name.split(" ")[0]} — your enquiry has landed in our inbox.
                    Expect a call or email within 24–48 hours. Urgent stock protection?
                    Ring the line directly.
                  </p>
                  <button
                    onClick={() => {
                      setForm(EMPTY);
                      setSent(false);
                    }}
                    data-testid="contact-send-another-button"
                    className="mt-8 border border-bone/30 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-thermal hover:text-thermal active:scale-95"
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                        Name *
                      </label>
                      <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                        Email *
                      </label>
                      <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-email-confirm" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                        Confirm Email *
                      </label>
                      <input id="contact-email-confirm" data-testid="contact-email-confirm-input" required type="email" value={form.confirmEmail} onChange={set("confirmEmail")} placeholder="Re-enter your email" className={inputCls} />
                    </div>
                  </div>
                  {errors.email && (
                    <p data-testid="contact-email-error" className="font-mono text-[11px] tracking-[0.1em] text-infrared">{errors.email}</p>
                  )}
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                        Phone
                      </label>
                      <input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="e.g. 0466 747 214 or 03 9876 5432" className={inputCls} />
                      <p className="mt-1.5 font-mono text-[10px] tracking-[0.05em] text-euca/70">Australian mobile or landline format</p>
                      {errors.phone && (
                        <p data-testid="contact-phone-error" className="mt-1 font-mono text-[11px] tracking-[0.1em] text-infrared">{errors.phone}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="contact-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                        Service Needed *
                      </label>
                      <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                        {SERVICES.map((s) => (
                          <option key={s} value={s} className="bg-obsidian">
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-location" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                      Property Location
                    </label>
                    <input id="contact-location" data-testid="contact-location-input" value={form.location} onChange={set("location")} placeholder="Town / district, VIC" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                      The Situation *
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      required
                      minLength={10}
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Species you're seeing, rough numbers, property size, and how urgent it is."
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-form-submit-button"
                    className="flex w-full items-center justify-center gap-2 border border-thermal bg-thermal px-6 py-4 font-mono text-xs font-medium uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-[0.98] disabled:opacity-50"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send Enquiry"
                    )}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="space-y-4">
              <div className="border border-range/30 bg-field p-8" data-testid="urgent-line-panel">
                <div className="flex items-center gap-3">
                  <Siren className="h-5 w-5 text-range" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-range">Urgent — Stock Under Attack</p>
                </div>
                <a href="tel:+61466747214" data-testid="urgent-phone-link" className="mt-4 block font-display text-3xl font-black tracking-tight text-bone transition-colors hover:text-thermal">
                  0466 747 214
                </a>
                <p className="mt-3 text-sm leading-relaxed text-euca">
                  Dogs in the ewes or pigs in the crop right now? Call direct — priority
                  response for active livestock protection.
                </p>
              </div>

              <div className="border border-thermal/15 bg-field p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">General Enquiries</p>
                <ul className="mt-5 space-y-4 text-sm text-bone">
                  <li className="flex items-center gap-3">
                    <Mail className="h-4 w-4 shrink-0 text-thermal" />
                    <a href="mailto:mick@vicpestandgame.com.au" data-testid="contact-email-link" className="break-all transition-colors hover:text-thermal">
                      mick@vicpestandgame.com.au
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="h-4 w-4 shrink-0 text-thermal" />
                    <a href="tel:+61466747214" data-testid="contact-phone-link" className="transition-colors hover:text-thermal">
                      0466 747 214
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 shrink-0 text-thermal" />
                    <span>Servicing Victoria &amp; Southern NSW</span>
                  </li>
                </ul>
                <p className="mt-6 border-t border-thermal/10 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-euca">
                  Response within 24–48 hours, business days
                </p>
              </div>

              <div className="border border-thermal/15 bg-field p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">Operating Regions</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <span key={r} className="border border-bone/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-euca">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
